import {
  useContext,
  useRef,
  useCallback,
  useEffect,
  useState,
  FC,
  ReactNode,
} from 'react';
import type { ChatHandle } from '@crono-one/ai-chat';
import { useAuth } from 'context/auth';
import { useRoleCapabilities } from 'hooks/useRoleCapabilities';
import usePatchUserPreferences from 'hooks/services/user/usePatchUserPreferences';
import { captureException } from 'utils/exceptionUtils';
import { ChatContext, SendMessageParams } from './contexts';

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within ChatProvider');
  }
  return context;
};

const ChatProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const chatRef = useRef<ChatHandle | null>(null);
  const { user } = useAuth();
  const { hasAppAccess } = useRoleCapabilities();
  const { mutateAsync: patchUserPreferences } = usePatchUserPreferences();

  const subHasCoreAI =
    !!user?.otherSettings?.hasCoreAI &&
    !!user?.userPreferences?.isCoreAIEnabled;

  const [chatSidebarOpen, setChatSidebarOpen] = useState(
    (user?.userPreferences?.showCoreAIChat && hasAppAccess) ?? false,
  );

  // Sync local state with user preferences on user change
  useEffect(() => {
    if (user?.userPreferences?.showCoreAIChat !== undefined) {
      setChatSidebarOpen(
        (user?.userPreferences?.showCoreAIChat && hasAppAccess) ?? false,
      );
    }
  }, [user?.userPreferences?.showCoreAIChat, hasAppAccess]);

  //#region Chat sidebar controls
  const setChatOpen = async (open: boolean) => {
    setChatSidebarOpen(open);

    await patchUserPreferences({
      showCoreAIChat: open,
    });

    if (user) {
      user.userPreferences = {
        ...user.userPreferences,
        showCoreAIChat: open,
      };
    }
  };

  const toggleChat = async () => {
    await setChatOpen(!chatSidebarOpen);
  };

  const openChat = async () => {
    if (chatSidebarOpen) return;
    await setChatOpen(true);
  };
  //#endregion

  const sendMessage = async ({ message }: SendMessageParams) => {
    if (!chatRef.current) {
      const errorMessage = 'Chat reference is not available during sendMessage';
      console.error(errorMessage);
      captureException(errorMessage, {
        level: 'error',
      });
      return;
    }
    await openChat();
    await chatRef.current.sendMessage(message);
  };

  const setInputText = async (text: string) => {
    if (!chatRef.current) {
      const errorMessage =
        'Chat reference is not available during setInputText';
      console.error(errorMessage);
      captureException(errorMessage, {
        level: 'error',
      });
      return;
    }
    await openChat();
    chatRef.current.setInputText(text);
  };

  return (
    <ChatContext.Provider
      value={{
        chatRef,
        chatSidebarOpen,
        toggleChat,
        openChat,
        subHasCoreAI,
        sendMessage,
        setInputText,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export default ChatProvider;
